/**
 * useTeamChat — shared team-chat data layer (TEAMCHAT-WEB-CONSOLIDATE, #2341)
 *
 * Extracted from TeamChatPage.tsx's proven SSE data layer so that both the
 * full-page `/chat` route (TeamChatPage) and the floating FAB panel
 * (ChatFABPanel → TeamChatEmbed) consume ONE canonical implementation instead
 * of two independently-evolved copies (the second of which had drifted — its
 * reaction endpoint 404'd, and it had no SSE so message-moves only surfaced on
 * the next 5s poll tick; see docs/features/teamchat-web-consolidate.md).
 *
 * What this hook owns: the channel list, the active channel, the active
 * channel's message stream, team users (for @mentions / DM picker), and every
 * action + SSE subscription that mutates that stream (send / edit / delete /
 * react / pin / move / mark-read / mark-unread / open-DM), plus the 5s poll
 * that backstops SSE. It deliberately does NOT own: composer UI state (input,
 * pending files, mention query), thread panels, the inbox, or any DOM refs —
 * those stay local to each consumer, because they diverge between the two
 * surfaces and are not the source of the duplication this consolidation targets.
 *
 * The 5s poll is retained (not removed) as a safety net — it is exactly what
 * TeamChatPage already runs in production alongside SSE. Removing it entirely is
 * a follow-up that needs the manual two-tab smoke test in the design doc's test
 * plan, which cannot be run headlessly.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useSSEEvent } from './useSSE'
import { authedFetch, getToken } from '../lib/api'

// ── Shared types (superset of the two former local copies) ──────────────────

export interface ChatChannel {
  id: number
  name: string
  display_name: string
  type: string
  entity_type: string | null
  entity_id: number | null
  is_private?: boolean
  unread_count: number
  last_message: string | null
  last_message_at: string | null
  inbound_email?: string
  email_bridge_enabled?: boolean
  is_assigned_project?: boolean
}

export interface ChatReaction {
  emoji: string
  count: number
  user_ids: number[]
}

export interface ChatAttachment {
  url: string
  filename: string
  size: number
  mime_type: string
}

export interface ChatMessage {
  id: number
  channel_id: number
  parent_id?: number | null
  user_id: number | null
  user_name: string
  content: string
  created_at: string
  edited_at: string | null
  reply_count?: number
  is_pinned?: boolean
  reactions: ChatReaction[]
  attachments?: ChatAttachment[]
  source?: string
  external_sender_name?: string
  external_sender_email?: string
}

export interface TeamUser {
  id: number
  name: string
  email?: string
}

// ── Pure reconcile helpers (the #986 anti-duplication core) ─────────────────
// Extracted so they can be unit-tested directly (see useTeamChat.test.ts). Both
// the 5s poll and every optimistic send route through these; a regression here
// is exactly the "whole thread duplicates on top of itself" symptom of #986.

/**
 * Merge a fresh server snapshot with the local list, preserving only optimistic
 * (negative-id) sends still in flight. A full snapshot swap — NOT append-by-
 * last-id — is required: once an optimistic temp is the last local row, every
 * fetched row looks "newer" than its negative id and the thread duplicates.
 */
export function reconcilePoll(prev: ChatMessage[], server: ChatMessage[]): ChatMessage[] {
  const pendingOptimistic = prev.filter(m => m.id < 0)
  return [...server, ...pendingOptimistic]
}

/** True if the server snapshot contains an id not already present locally. */
export function hasNewMessages(prev: ChatMessage[], server: ChatMessage[]): boolean {
  const prevIds = new Set(prev.map(m => m.id))
  return server.some(m => !prevIds.has(m.id))
}

/**
 * Resolve an optimistic send once its real row returns from POST: if a poll
 * tick already inserted the real row, just drop the temp; otherwise swap the
 * temp in place. Guards the send-vs-poll race.
 */
export function reconcileSentMessage(prev: ChatMessage[], real: ChatMessage, tempId: number): ChatMessage[] {
  return prev.some(m => m.id === real.id)
    ? prev.filter(m => m.id !== tempId)
    : prev.map(m => m.id === tempId ? real : m)
}

// ── JWT identity (mirrors TeamChatPage.getMyUserId / getMyRole) ─────────────

function getMyUserId(): number | null {
  try {
    const token = getToken()
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.sub || payload.user_id || null
  } catch {
    return null
  }
}

function getMyRole(): string {
  try {
    const token = getToken()
    if (!token) return ''
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.role || ''
  } catch {
    return ''
  }
}

export interface UseTeamChatOptions {
  /**
   * Initial active-channel behavior:
   *  - 'general' → auto-select the `general` channel (or the first one) once the
   *    channel list loads, matching the FAB panel's current behavior.
   *  - 'none'    → leave nothing selected; the caller drives selection (e.g.
   *    TeamChatPage's `?channel=` URL wiring).
   */
  initialChannel?: 'general' | 'none'
  /**
   * Whether a 401 on a chat request should redirect the whole app to login.
   * The page (a route) defaults to true; the global FAB passes false so a
   * transient chat auth failure never yanks the user out of whatever they're on.
   */
  redirectOnAuthFailure?: boolean
  /** Fired after the active channel is opened + its unread badge cleared. */
  onRead?: () => void
  /** Scroll the message list; the consumer owns the DOM ref. */
  onScrollToBottom?: (behavior: 'auto' | 'smooth') => void
}

export interface UseTeamChat {
  channels: ChatChannel[]
  setChannels: Dispatch<SetStateAction<ChatChannel[]>>
  activeChannel: ChatChannel | null
  setActiveChannel: Dispatch<SetStateAction<ChatChannel | null>>
  selectChannel: (ch: ChatChannel) => void
  messages: ChatMessage[]
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>
  hasMore: boolean
  loading: boolean
  teamUsers: TeamUser[]
  myUserId: number | null
  myRole: string
  isAdmin: boolean
  loadChannels: () => Promise<void>
  loadMessages: (channelId: number, before?: number) => Promise<void>
  sendMessage: (args: { content: string; attachments?: ChatAttachment[]; clientToken?: string }) => Promise<void>
  saveEdit: (msg: ChatMessage, content: string) => Promise<ChatMessage | null>
  deleteMessage: (msg: ChatMessage) => Promise<void>
  togglePin: (msg: ChatMessage) => Promise<void>
  toggleReact: (msg: ChatMessage, emoji: string) => Promise<void>
  moveMessage: (msg: ChatMessage, targetChannelId: number) => Promise<void>
  openDM: (user: TeamUser) => Promise<ChatChannel | null>
  markRead: (channelId: number) => Promise<void>
  markUnread: (channelId: number) => Promise<void>
}

export function useTeamChat(opts: UseTeamChatOptions = {}): UseTeamChat {
  const {
    initialChannel = 'none',
    redirectOnAuthFailure = true,
    onRead,
    onScrollToBottom,
  } = opts

  const [channels, setChannels] = useState<ChatChannel[]>([])
  const [activeChannel, setActiveChannel] = useState<ChatChannel | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([])

  const myUserId = getMyUserId()
  const myRole = getMyRole()
  const isAdmin = myRole === 'admin' || myRole === 'super_admin'

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const activeChannelRef = useRef<ChatChannel | null>(null)
  activeChannelRef.current = activeChannel

  // Keep the newest callbacks reachable from long-lived effects without making
  // them effect deps (which would tear down/rebuild the poll + SSE each render).
  const onReadRef = useRef(onRead)
  onReadRef.current = onRead
  const onScrollRef = useRef(onScrollToBottom)
  onScrollRef.current = onScrollToBottom

  // ── fetch layer (mirrors TeamChatPage.chatFetch, redirect configurable) ───
  const chatFetch = useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const res = await authedFetch(path, init, { redirectOnAuthFailure })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(`${res.status}: ${text}`)
    }
    if (res.status === 204) return undefined as unknown as T
    return res.json() as Promise<T>
  }, [redirectOnAuthFailure])

  // ── channel list ──────────────────────────────────────────────────────────
  const loadChannels = useCallback(async () => {
    try {
      const data = await chatFetch<ChatChannel[]>('/api/v1/chat/channels')
      if (Array.isArray(data)) setChannels(data)
    } catch (e) {
      console.error('useTeamChat: failed to load channels', e)
    }
  }, [chatFetch])

  // Load channels on mount + refresh on window focus (catch events missed while
  // the tab was backgrounded / the SSE stream was disconnected).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await chatFetch<ChatChannel[]>('/api/v1/chat/channels')
        if (cancelled || !Array.isArray(data)) return
        setChannels(data)
        if (initialChannel === 'general') {
          const general = data.find(c => c.name === 'general') || data[0]
          if (general) setActiveChannel(general)
        }
      } catch { /* silent */ }
    })()
    const onFocus = () => { loadChannels() }
    window.addEventListener('focus', onFocus)
    return () => { cancelled = true; window.removeEventListener('focus', onFocus) }
    // initialChannel is a mount-time constant; loadChannels is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatFetch])

  // Team users (for @mentions + DM picker)
  useEffect(() => {
    chatFetch<TeamUser[]>('/api/v1/users')
      .then(data => { if (Array.isArray(data)) setTeamUsers(data) })
      .catch(() => {})
  }, [chatFetch])

  // ── messages: initial load / pagination (replace or prepend) ──────────────
  const loadMessages = useCallback(async (channelId: number, before?: number) => {
    setLoading(true)
    try {
      const url = before
        ? `/api/v1/chat/channels/${channelId}/messages?limit=50&before=${before}`
        : `/api/v1/chat/channels/${channelId}/messages?limit=50`
      const data = await chatFetch<{ messages: ChatMessage[]; has_more: boolean }>(url)
      if (data && Array.isArray(data.messages)) {
        if (before) {
          setMessages(prev => [...data.messages, ...prev])
        } else {
          setMessages(data.messages)
          setTimeout(() => onScrollRef.current?.('auto'), 50)
        }
        setHasMore(data.has_more || false)
      }
    } catch (e) {
      console.error('useTeamChat: failed to load messages', e)
    } finally {
      setLoading(false)
    }
  }, [chatFetch])

  // Poll reconcile (the #986 fix): swap in the fresh server snapshot but keep
  // any optimistic (negative-id) sends still in flight. Comparing against prev's
  // last id duplicated the whole thread once an optimistic temp was the last row.
  const pollMessages = useCallback(async (channelId: number) => {
    try {
      const data = await chatFetch<{ messages: ChatMessage[]; has_more: boolean }>(
        `/api/v1/chat/channels/${channelId}/messages?limit=50`
      )
      if (data && Array.isArray(data.messages)) {
        setMessages(prev => {
          const pendingOptimistic = prev.filter(m => m.id < 0)
          const prevIds = new Set(prev.map(m => m.id))
          const hasNew = data.messages.some(m => !prevIds.has(m.id))
          if (hasNew) setTimeout(() => onScrollRef.current?.('smooth'), 50)
          return [...data.messages, ...pendingOptimistic]
        })
      }
    } catch { /* silent */ }
  }, [chatFetch])

  // On active-channel change: initial load, then install the 5s poll backstop.
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (!activeChannel) return
    loadMessages(activeChannel.id)
    pollRef.current = setInterval(() => {
      const ch = activeChannelRef.current
      if (ch) pollMessages(ch.id)
    }, 5000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [activeChannel, loadMessages, pollMessages])

  // Mark read on channel open: clear server unread, zero the local badge, notify.
  useEffect(() => {
    if (!activeChannel) return
    chatFetch(`/api/v1/chat/channels/${activeChannel.id}/read`, { method: 'POST', body: JSON.stringify({}) }).catch(() => {})
    setChannels(prev => prev.map(ch => ch.id === activeChannel.id ? { ...ch, unread_count: 0 } : ch))
    onReadRef.current?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel?.id])

  const selectChannel = useCallback((ch: ChatChannel) => {
    setActiveChannel(ch)
    setMessages([])
  }, [])

  // ── actions ────────────────────────────────────────────────────────────────

  const sendMessage = useCallback(async ({ content, attachments, clientToken }: { content: string; attachments?: ChatAttachment[]; clientToken?: string }) => {
    const ch = activeChannelRef.current
    if (!ch) return
    const tempId = -(Date.now())
    const optimistic: ChatMessage = {
      id: tempId,
      channel_id: ch.id,
      user_id: myUserId,
      user_name: 'You',
      content,
      created_at: new Date().toISOString(),
      edited_at: null,
      reactions: [],
      attachments: attachments && attachments.length ? attachments : [],
    }
    setMessages(prev => [...prev, optimistic])
    setTimeout(() => onScrollRef.current?.('smooth'), 50)
    try {
      const body: Record<string, unknown> = { content }
      if (attachments && attachments.length) body.attachments = attachments
      // Carried through by an offline-outbox drain replay (#2463) so the
      // server can dedup a send whose original response was lost — never
      // set by the interactive Send button's own happy path.
      if (clientToken) body.client_token = clientToken
      const real = await chatFetch<ChatMessage>(`/api/v1/chat/channels/${ch.id}/messages`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      setMessages(prev => prev.some(m => m.id === real.id)
        ? prev.filter(m => m.id !== tempId)
        : prev.map(m => m.id === tempId ? real : m))
    } catch (e) {
      // Remove the optimistic row and re-throw so the caller can restore its
      // own composer state (input text, staged files).
      setMessages(prev => prev.filter(m => m.id !== tempId))
      throw e
    }
  }, [chatFetch, myUserId])

  const saveEdit = useCallback(async (msg: ChatMessage, content: string): Promise<ChatMessage | null> => {
    const ch = activeChannelRef.current
    if (!ch) return null
    try {
      const updated = await chatFetch<ChatMessage>(
        `/api/v1/chat/channels/${ch.id}/messages/${msg.id}`,
        { method: 'PATCH', body: JSON.stringify({ content }) }
      )
      setMessages(prev => prev.map(m => m.id === msg.id ? updated : m))
      return updated
    } catch (e) {
      console.error('useTeamChat: failed to edit message', e)
      return null
    }
  }, [chatFetch])

  const deleteMessage = useCallback(async (msg: ChatMessage) => {
    const ch = activeChannelRef.current
    if (!ch) return
    try {
      await chatFetch(`/api/v1/chat/channels/${ch.id}/messages/${msg.id}`, { method: 'DELETE' })
      setMessages(prev => prev.filter(m => m.id !== msg.id))
    } catch (e) {
      console.error('useTeamChat: failed to delete message', e)
    }
  }, [chatFetch])

  const togglePin = useCallback(async (msg: ChatMessage) => {
    try {
      if (msg.is_pinned) {
        await chatFetch(`/api/v1/chat/messages/${msg.id}/pin`, { method: 'DELETE' })
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_pinned: false } : m))
      } else {
        await chatFetch(`/api/v1/chat/messages/${msg.id}/pin`, { method: 'POST', body: JSON.stringify({}) })
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_pinned: true } : m))
      }
    } catch (e) {
      console.error('useTeamChat: failed to toggle pin', e)
    }
  }, [chatFetch])

  const toggleReact = useCallback(async (msg: ChatMessage, emoji: string) => {
    try {
      const alreadyReacted = myUserId != null && (msg.reactions || []).some(
        r => r.emoji === emoji && r.user_ids.includes(myUserId)
      )
      if (alreadyReacted) {
        await chatFetch(`/api/v1/chat/messages/${msg.id}/reactions/${encodeURIComponent(emoji)}`, { method: 'DELETE' })
        setMessages(prev => prev.map(m => {
          if (m.id !== msg.id) return m
          const reactions = (m.reactions || [])
            .map(r => r.emoji !== emoji ? r : { ...r, count: r.count - 1, user_ids: r.user_ids.filter(id => id !== myUserId) })
            .filter(r => r.count > 0)
          return { ...m, reactions }
        }))
      } else {
        await chatFetch(`/api/v1/chat/messages/${msg.id}/reactions`, { method: 'POST', body: JSON.stringify({ emoji }) })
        setMessages(prev => prev.map(m => {
          if (m.id !== msg.id) return m
          const existing = (m.reactions || []).find(r => r.emoji === emoji)
          const reactions = existing
            ? (m.reactions || []).map(r => r.emoji === emoji
                ? { ...r, count: r.count + 1, user_ids: [...r.user_ids, myUserId!] }
                : r)
            : [...(m.reactions || []), { emoji, count: 1, user_ids: [myUserId!] }]
          return { ...m, reactions }
        }))
      }
    } catch (e) {
      console.error('useTeamChat: failed to toggle reaction', e)
    }
  }, [chatFetch, myUserId])

  const moveMessage = useCallback(async (msg: ChatMessage, targetChannelId: number) => {
    try {
      await chatFetch(`/api/v1/chat/messages/${msg.id}/move`, {
        method: 'PATCH',
        body: JSON.stringify({ target_channel_id: targetChannelId }),
      })
      setMessages(prev => prev.filter(m => m.id !== msg.id))
    } catch (e) {
      console.error('useTeamChat: failed to move message', e)
    }
  }, [chatFetch])

  const openDM = useCallback(async (user: TeamUser): Promise<ChatChannel | null> => {
    try {
      const ch = await chatFetch<ChatChannel>('/api/v1/chat/dm', {
        method: 'POST',
        body: JSON.stringify({ user_id: user.id }),
      })
      setChannels(prev => prev.some(c => c.id === ch.id) ? prev : [...prev, ch])
      selectChannel(ch)
      return ch
    } catch (e) {
      console.error('useTeamChat: failed to open DM', e)
      return null
    }
  }, [chatFetch, selectChannel])

  const markRead = useCallback(async (channelId: number) => {
    try {
      await chatFetch(`/api/v1/chat/channels/${channelId}/read`, { method: 'POST', body: JSON.stringify({}) })
      setChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, unread_count: 0 } : ch))
    } catch { /* non-fatal */ }
  }, [chatFetch])

  const markUnread = useCallback(async (channelId: number) => {
    try {
      await chatFetch(`/api/v1/chat/channels/${channelId}/mark-unread`, { method: 'POST' })
      setChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, unread_count: Math.max(ch.unread_count, 1) } : ch))
    } catch (e) {
      console.error('useTeamChat: failed to mark unread', e)
    }
  }, [chatFetch])

  // ── SSE (lifted verbatim from TeamChatPage's proven handlers) ─────────────

  useSSEEvent('reaction', useCallback((event) => {
    const p = event.payload as {
      message_id: number; channel_id: number
      emoji: string; user_id: number; action: 'add' | 'remove'
    }
    if (p.channel_id !== activeChannel?.id) return
    if (p.user_id === myUserId) return // already handled optimistically
    setMessages(prev => prev.map(m => {
      if (m.id !== p.message_id) return m
      const reactions = m.reactions || []
      if (p.action === 'add') {
        const existing = reactions.find(r => r.emoji === p.emoji)
        if (existing) {
          return { ...m, reactions: reactions.map(r =>
            r.emoji === p.emoji
              ? { ...r, count: r.count + 1, user_ids: [...r.user_ids, p.user_id] }
              : r
          )}
        }
        return { ...m, reactions: [...reactions, { emoji: p.emoji, count: 1, user_ids: [p.user_id] }] }
      } else {
        return { ...m, reactions: reactions
          .map(r => r.emoji === p.emoji
            ? { ...r, count: r.count - 1, user_ids: r.user_ids.filter(id => id !== p.user_id) }
            : r)
          .filter(r => r.count > 0)
        }
      }
    }))
  }, [activeChannel?.id, myUserId]))

  useSSEEvent('new_message', useCallback((event) => {
    const p = event.payload as { channel_id: number; message: ChatMessage }
    if (p.channel_id !== activeChannel?.id) return
    if (p.message.user_id === myUserId) return // own send already appended
    setMessages(prev => prev.some(m => m.id === p.message.id) ? prev : [...prev, p.message])
    setTimeout(() => onScrollRef.current?.('smooth'), 50)
  }, [activeChannel?.id, myUserId]))

  useSSEEvent('message_moved', useCallback((event) => {
    const p = event.payload as { message_id: number; source_channel_id: number; target_channel_id: number; message: ChatMessage }
    if (p.source_channel_id === activeChannel?.id) {
      setMessages(prev => prev.filter(m => m.id !== p.message_id))
    } else if (p.target_channel_id === activeChannel?.id) {
      setMessages(prev => prev.some(m => m.id === p.message_id) ? prev : [...prev, p.message])
    }
  }, [activeChannel?.id]))

  useSSEEvent('thread_reply', useCallback((event) => {
    const p = event.payload as { channel_id: number; parent_id: number; reply_count: number }
    if (p.channel_id !== activeChannel?.id) return
    setMessages(prev => prev.map(m => m.id === p.parent_id ? { ...m, reply_count: p.reply_count } : m))
  }, [activeChannel?.id]))

  useSSEEvent('message_pinned', useCallback((event) => {
    const p = event.payload as { channel_id: number; message_id: number }
    if (p.channel_id !== activeChannel?.id) return
    setMessages(prev => prev.map(m => m.id === p.message_id ? { ...m, is_pinned: true } : m))
  }, [activeChannel?.id]))

  useSSEEvent('message_unpinned', useCallback((event) => {
    const p = event.payload as { channel_id: number; message_id: number }
    if (p.channel_id !== activeChannel?.id) return
    setMessages(prev => prev.map(m => m.id === p.message_id ? { ...m, is_pinned: false } : m))
  }, [activeChannel?.id]))

  return {
    channels, setChannels,
    activeChannel, setActiveChannel, selectChannel,
    messages, setMessages,
    hasMore, loading, teamUsers,
    myUserId, myRole, isAdmin,
    loadChannels, loadMessages,
    sendMessage, saveEdit, deleteMessage, togglePin, toggleReact, moveMessage,
    openDM, markRead, markUnread,
  }
}
